import React, { useState, useMemo } from 'react';
import { Plus, Calendar, User, Trash2, Filter } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProductionModal from '../components/modals/ProductionModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import { useAppStore } from '../store';
import { Production } from '../types';
import { format, addDays, isAfter, isBefore, parseISO } from 'date-fns';
import Select from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';

const Productions: React.FC = () => {
  const { productions, clients, salespersons, addProduction, updateProduction, deleteProduction } = useAppStore();
  const { isAdmin } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState<Production | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productionToDelete, setProductionToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // ─── helper ───
const clearProductionDraft = () => {
  localStorage.removeItem('production_approved_file');
  localStorage.removeItem('production_dtf_file');
  localStorage.removeItem('production_embroidery_file');
  localStorage.removeItem('production_payment_receipt');
  localStorage.removeItem('production_form_data');
  localStorage.removeItem('production_file_names');
};

  
  // State for column clear confirmation
  const [clearColumnConfirmOpen, setClearColumnConfirmOpen] = useState(false);
  const [columnToClear, setColumnToClear] = useState<string | null>(null);

  // Filters
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const handleDragStart = (e: React.DragEvent, production: Production) => {
    e.dataTransfer.setData('productionId', production.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, status: Production['status']) => {
    e.preventDefault();
    const productionId = e.dataTransfer.getData('productionId');
    
    try {
      await updateProduction(productionId, { status });
    } catch (error) {
      console.error('Error updating production status:', error);
      setError('Error al actualizar el estado de la producción');
      setShowErrorModal(true);
    }
  };

  // Apply filters to productions
  const filteredProductions = useMemo(() => {
    return productions.filter(production => {
      // Filter by client
      if (clientFilter !== 'all' && production.client_id !== clientFilter) {
        return false;
      }

      // Filter by salesperson
      if (salespersonFilter !== 'all' && production.salesperson !== salespersonFilter) {
        return false;
      }

      // Filter by delivery date
      if (deliveryDateFilter !== 'all') {
        const today = new Date();
        const deliveryDate = parseISO(production.delivery_date);
        
        switch (deliveryDateFilter) {
          case '2days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 2));
          case '7days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 7));
          case '15days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 15));
          default:
            return true;
        }
      }

      return true;
    });
  }, [productions, clientFilter, salespersonFilter, deliveryDateFilter]);

  const getColumnProductions = (status: Production['status']) => 
    filteredProductions.filter(prod => prod.status === status);

  const handleEditProduction = (production: Production) => {
    setSelectedProduction(production);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setProductionToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productionToDelete) return;

    try {
      await deleteProduction(productionToDelete);
    } catch (error) {
      console.error('Error deleting production:', error);
      setError('Error al eliminar la producción');
      setShowErrorModal(true);
    }
  };

  const handleSaveProduction = async (production: Omit<Production, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (selectedProduction) {
        await updateProduction(selectedProduction.id, production);
      } else {
        await addProduction(production);
      }
      setIsModalOpen(false);
      setSelectedProduction(undefined);
    } catch (error) {
      console.error('Error saving production:', error);
      setError('Error al guardar la producción');
      setShowErrorModal(true);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setClientFilter('all');
    setSalespersonFilter('all');
    setDeliveryDateFilter('all');
  };

  // Handle clear column
  const handleClearColumn = (status: string) => {
    setColumnToClear(status);
    setClearColumnConfirmOpen(true);
  };

  // Confirm clear column
  const confirmClearColumn = async () => {
    if (!columnToClear) return;
    
    try {
      // Get all production IDs for the selected status
      const productionIds = filteredProductions
        .filter(production => production.status === columnToClear)
        .map(production => production.id);
      
      // Delete each production
      for (const id of productionIds) {
        await deleteProduction(id);
      }
      
      setClearColumnConfirmOpen(false);
      setColumnToClear(null);
    } catch (error: any) {
      console.error('Error clearing column:', error);
      setError(error.message || 'Error al eliminar las producciones');
      setShowErrorModal(true);
    }
  };

  const renderProduction = (production: Production) => {
    const client = clients.find(c => c.id === production.client_id);
    
    const handleClick = (e: React.MouseEvent) => {
      // Only handle click if it's not a drag operation
      if (e.detail === 1) { // Single click
        e.preventDefault();
        e.stopPropagation();
        handleEditProduction(production);
      }
    };
    
    return (
      <div
        key={production.id}
        draggable
        onDragStart={(e) => handleDragStart(e, production)}
        onClick={handleClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.cursor = 'pointer';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.cursor = 'grab';
        }}
        className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm cursor-grab hover:shadow-md hover:scale-[1.02] transition-all duration-200 relative group select-none border border-transparent hover:border-blue-200 dark:hover:border-blue-700"
        title="Click para editar o arrastrar para mover"
      >
        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening edit modal
            handleDeleteClick(production.id);
          }}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        <h4 className="font-medium text-gray-900 dark:text-white">{production.name}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{client?.name || 'Cliente no encontrado'}</p>
        <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <Calendar className="h-4 w-4 mr-1" />
          {format(new Date(production.delivery_date), 'dd/MM/yyyy')}
        </div>
        <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <User className="h-4 w-4 mr-1" />
          {production.salesperson}
        </div>
        {production.comment && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{production.comment}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Producciones</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              clearProductionDraft();
              setSelectedProduction(undefined);
              setIsModalOpen(true);
            }}
          >
            Nueva Producción
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-40">
                <Select
                  label="Cliente"
                  value={clientFilter}
                  onChange={setClientFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...clients.map(client => ({
                      value: client.id,
                      label: client.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Vendedor"
                  value={salespersonFilter}
                  onChange={setSalespersonFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...salespersons.map(sp => ({
                      value: sp.name,
                      label: sp.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Entrega"
                  value={deliveryDateFilter}
                  onChange={setDeliveryDateFilter}
                  options={[
                    { value: 'all', label: 'Todas' },
                    { value: '2days', label: 'Próx. 2 días' },
                    { value: '7days', label: 'Próx. 7 días' },
                    { value: '15days', label: 'Próx. 15 días' }
                  ]}
                />
              </div>
              
              <div className="flex items-center">
                <Button
                  variant="outline"
                  icon={<Filter className="h-4 w-4" />}
                  onClick={resetFilters}
                  className="h-12 px-3"
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Tablero de Producción" />
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending Column */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'pending')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Pendiente <span className="font-normal text-sm">({getColumnProductions('pending').length})</span>
                </h3>
                {isAdmin && getColumnProductions('pending').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('pending')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las producciones pendientes"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnProductions('pending').map(renderProduction)}
                {getColumnProductions('pending').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay producciones pendientes
                  </div>
                )}
              </div>
            </div>

            {/* In Progress Column */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'in_progress')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  En Proceso <span className="font-normal text-sm">({getColumnProductions('in_progress').length})</span>
                </h3>
                {isAdmin && getColumnProductions('in_progress').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('in_progress')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las producciones en proceso"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnProductions('in_progress').map(renderProduction)}
                {getColumnProductions('in_progress').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay producciones en proceso
                  </div>
                )}
              </div>
            </div>

            {/* Finished Column */}
            <div 
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'finished')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Terminado <span className="font-normal text-sm">({getColumnProductions('finished').length})</span>
                </h3>
                {isAdmin && getColumnProductions('finished').length > 0 && (
                  <button
                    onClick={() => handleClearColumn('finished')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las producciones terminadas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {getColumnProductions('finished').map(renderProduction)}
                {getColumnProductions('finished').length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay producciones terminadas
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ProductionModal
        isOpen={isModalOpen}
        onClose={() => {
          clearProductionDraft();
          setIsModalOpen(false);
          setSelectedProduction(undefined);
        }}
        onSave={handleSaveProduction}
        initialData={selectedProduction}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setProductionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Producción"
        message="¿Estás seguro de que deseas eliminar esta producción? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={clearColumnConfirmOpen}
        onClose={() => {
          setClearColumnConfirmOpen(false);
          setColumnToClear(null);
        }}
        onConfirm={confirmClearColumn}
        title={`Eliminar todas las producciones ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' : 'terminadas'
        }`}
        message={`¿Estás seguro de que deseas eliminar todas las producciones ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' : 'terminadas'
        }? Esta acción no se puede deshacer.`}
        confirmText="Eliminar todas"
        variant="danger"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default Productions;